package music.limasoul.client.shared.network

import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsChannel
import io.ktor.http.HttpStatusCode
import io.ktor.http.encodedPath
import io.ktor.serialization.kotlinx.json.DefaultJson
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import music.jammers.client.shared.auth.domain.repository.TokenRepository
import music.limasoul.shared.network.domain.model.AppException
import music.limasoul.shared.network.domain.model.AuthException
import music.limasoul.shared.network.domain.model.ErrorWrapper

object HttpClientFactory {

    private val validStatuses = listOf(HttpStatusCode.OK, HttpStatusCode.Created)

    fun create(
        baseUrl: String,
        enableLogging: Boolean,
        log: Logger,
        errorMapper: ErrorMapper,
        tokenRepository: TokenRepository,
    ): HttpClient = HttpClient {
        defaultRequest {
            url(baseUrl)
        }

        if (enableLogging) {
            install(Logging) {
                level = LogLevel.ALL
                logger = log
            }
        }

        install(ContentNegotiation) {
            json(
                Json {
                    prettyPrint = true
                    isLenient = true
                    ignoreUnknownKeys = true
                }
            )
        }

        install(Auth) {
            bearer {
                sendWithoutRequest { requestBuilder ->
                    log.log("path: ${requestBuilder.url.encodedPath}")
                    !requestBuilder.url.encodedPath.endsWith("/login")
                }

                loadTokens {
                    val token = tokenRepository.get()
                    BearerTokens(token?.value ?: "", "")
                }

                refreshTokens {
                    val token = tokenRepository.get()
                    BearerTokens(token?.value ?: "", "")
                }
            }
        }

        HttpResponseValidator {
            validateResponse { response ->
                if (response.status !in validStatuses) {
                    val exception = findError(response, log, errorMapper)

                    if (exception != null) {
                        throw exception
                    }
                }
            }
        }
    }

    private suspend fun findError(response: HttpResponse, logger: Logger, errorMapper: ErrorMapper): Throwable? {
        val content = response.bodyAsChannel()
            .readRemaining()
            .copy()
            .readText()

        logger.log("findError: content -> $content")

        if (response.status == HttpStatusCode.Unauthorized) {
            logger.log("findError: 401 Unauthorized")
            return AuthException
        }

        return try {
            val errorWrapper: ErrorWrapper = DefaultJson.decodeFromString(ErrorWrapper.serializer(), content)
            val error = errorWrapper.error

            logger.log("after parse content, ${error.status}; ${error.message}")
            errorMapper.map(error)
        } catch (ignore: SerializationException) {
            logger.log("Can't to find error field in content: '$content'")
            AppException(response.status, message = response.status.description)
        } catch (expected: Throwable) {
            logger.log("Error: $expected")
            throw expected
        }
    }
}